// components/shop/CartControls.jsx
// Shop buyer-page cart UI, shared by Uganda's Shop tab (pages/Market.jsx) and
// the DRC shop (pages/DwiSendShop.jsx). Display only — prices, stock and
// checkout stay in the pages and are re-checked by the server.
//   QtyStepper     − n + within min…max (the "X available" stock)
//   FloatingCart   fixed bottom-right button: icon, item count, running total
//   CartDrawer     phone: the cart as a slide-over; computer: the sticky side column
//   useSavedCart   the cart kept in this browser, per account and per shop
//   refreshCart    a cart re-checked against the shop's current prices and stock
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart, Minus, Plus, X } from 'lucide-react';

// ── Saved cart ───────────────────────────────────────────────────────────────
// Kept in this browser's localStorage under one key per shop and account
// ("dwi.cart.v1:uganda:<userId>"), so it survives a reload or leaving the page
// and never mixes shops or buyers. Only ids, quantities and the price last
// seen are kept; everything is re-checked against the shop when it loads and
// again before an order is sent. No account (key null) = not saved.
const CART_PREFIX = 'dwi.cart.v1:';
const CART_MAX_AGE = 30 * 24 * 3600 * 1000;   // a cart untouched for 30 days is dropped
const CART_MAX_LINES = 50;

function readCart(key) {
  if (!key) return [];
  try {
    const saved = JSON.parse(localStorage.getItem(CART_PREFIX + key) || 'null');
    if (!saved || !Array.isArray(saved.items) || Date.now() - Number(saved.savedAt || 0) > CART_MAX_AGE) return [];
    return saved.items.filter(l => l && typeof l.productId === 'string' && typeof l.variantId === 'string'
      && Number.isInteger(l.qty) && l.qty > 0).slice(0, CART_MAX_LINES);
  } catch { return []; }
}

function writeCart(key, items) {
  if (!key) return;
  try {
    if (items.length) localStorage.setItem(CART_PREFIX + key, JSON.stringify({ savedAt: Date.now(), items }));
    else localStorage.removeItem(CART_PREFIX + key);
  } catch { /* storage full or blocked — the cart still works for this visit */ }
}

// Like useState([]) but loaded from / saved to the browser under `key`.
export function useSavedCart(key) {
  const [state, setState] = useState(() => ({ key, items: readCart(key) }));
  if (state.key !== key) setState({ key, items: readCart(key) });   // account changed: load that account's cart
  const setItems = useCallback(update => setState(s => ({ key: s.key, items: typeof update === 'function' ? update(s.items) : update })), []);
  useEffect(() => { writeCart(state.key, state.items); }, [state]);
  return [state.key === key ? state.items : readCart(key), setItems];
}

// Re-check cart lines against the shop's current products. Drops what is gone,
// out of stock or no longer priced; lowers quantities to what is available;
// takes the current price. `priceKey` is 'priceUGX' (Uganda) or 'priceMinor' (DRC).
// Returns the new lines and the names of what changed, so the page can say so.
export function refreshCart(lines, products, priceKey) {
  const removed = [], repriced = [], reduced = [];
  const next = [];
  for (const l of lines) {
    const p = products.find(x => x._id === l.productId);
    const v = p?.variants.find(x => x._id === l.variantId);
    const label = p && v ? `${p.name} (${v.unitName})` : (l.name ? `${l.name}${l.unitName ? ` (${l.unitName})` : ''}` : '');
    const min = v?.minOrderQty || 1;
    if (!p || !v || p.priceMode === 'on_request' || v[priceKey] == null || !(v.inStock >= min)) { removed.push(label); continue; }
    const qty = Math.max(min, Math.min(v.inStock, l.qty));
    if (qty < l.qty) reduced.push(label);
    if (l[priceKey] != null && l[priceKey] !== v[priceKey]) repriced.push(label);
    next.push({ ...l, qty, [priceKey]: v[priceKey], name: p.name, unitName: v.unitName, min, max: v.inStock });
  }
  const same = next.length === lines.length && next.every((l, i) => l.qty === lines[i].qty && l[priceKey] === lines[i][priceKey]
    && l.max === lines[i].max && l.min === lines[i].min);
  return { lines: next, removed, repriced, reduced, changed: !!(removed.length || repriced.length || reduced.length), same };
}

// Tailwind's lg breakpoint — at and above it the cart is a visible side column.
export const isDesktop = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;

export function QtyStepper({ value, min = 1, max, onChange, lessLabel = 'Less', moreLabel = 'More' }) {
  const atMin = value <= min;
  const atMax = max != null && value >= max;
  const btn = 'w-7 h-7 flex items-center justify-center rounded-lg border border-border text-text-primary disabled:opacity-30 disabled:cursor-not-allowed hover:border-cyan';
  return (
    <div className="inline-flex items-center gap-1.5">
      <button type="button" className={btn} disabled={atMin} aria-label={lessLabel} onClick={() => onChange(value - 1)}><Minus size={14} /></button>
      <span className="min-w-[2rem] text-center text-sm font-semibold text-text-primary tabular-nums">{value}</span>
      <button type="button" className={btn} disabled={atMax} aria-label={moreLabel} onClick={() => onChange(value + 1)}><Plus size={14} /></button>
    </div>
  );
}

// Rendered on document.body so it stays fixed whatever the page's layout does.
export function FloatingCart({ count, total, label, onClick }) {
  return createPortal(
    <button type="button" onClick={onClick} aria-label={`${label} (${count})`}
      className="fixed right-4 bottom-4 z-40 flex items-center gap-2 rounded-full bg-cyan text-white shadow-glow-cyan pl-3 pr-4 py-2.5 hover:brightness-110 active:scale-95 transition"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}>
      <span className="relative">
        <ShoppingCart size={22} />
        <span className="absolute -top-2 -right-2.5 min-w-[1.25rem] h-5 px-1 rounded-full bg-white text-cyan text-[11px] font-black flex items-center justify-center tabular-nums">{count}</span>
      </span>
      <span className="text-sm font-bold tabular-nums ml-1">{total}</span>
    </button>,
    document.body,
  );
}

// The cart panel's wrapper. Closed on a phone it is hidden (the floating button
// opens it); open, it slides over the page from the right. On a computer it is
// always the sticky right-hand column, as before.
export function CartDrawer({ open, onClose, closeLabel = 'Close', panelRef, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <div ref={panelRef}
      className={open
        ? 'fixed inset-0 z-50 flex justify-end lg:z-auto lg:block lg:sticky lg:inset-auto lg:top-4'
        : 'hidden lg:block lg:sticky lg:top-4'}>
      {open && <div className="absolute inset-0 bg-black/60 lg:hidden" onClick={onClose} />}
      <div className={open
        ? 'relative h-full w-full max-w-md overflow-y-auto bg-bg p-3 animate-slide-up lg:h-auto lg:max-w-none lg:overflow-visible lg:bg-transparent lg:p-0 lg:animate-none'
        : ''}>
        {open && (
          <div className="flex justify-end mb-2 lg:hidden">
            <button type="button" onClick={onClose} aria-label={closeLabel}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-bg-elevated text-text-primary"><X size={18} /></button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
